import React from 'react';
import { observer } from 'mobx-react';
import snapshotsCss from './snapshots.css';

import {
    AdvanceTable,
    BasicTab,
    IconTextButton,
    ITableToolbarProps,
    Label,
    message,
    showConfirmDialog,
    Tooltip,
    Typography,
    IBasicTableProps,
} from 'ui-lib';
import { PublishSnapshotDrawer } from './publish-snapshot-drawer';
import { IDisplaySnapshotProps } from '../../../../store/snapshot.store';
import { useAuthStore, useSnapshotStore } from '../../../../store';

import { SNAPSHOT_STATUS } from '../../../../dto/software-snapshot-dto';
import { manageSnapshotPermission, useSnapshotContext } from '.';
import { FILTER_REPOSITORY_TYPE } from '../../../../dto/repository.dto';
import { TableUtils } from '../../components/table-utils';

function confirmDelete(code: string, action: any) {
    showConfirmDialog({
        title: 'Confirmation',
        content: 'Do you want to delete this release?',
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: () => action(code),
    });
}

export interface ISnapshotTableProps {
    snapshotType: SNAPSHOT_STATUS;
    setSnapshotType: (type: SNAPSHOT_STATUS) => void;
}

export const ManageSnapshotsTable = observer((props: ISnapshotTableProps) => {
    const context = useSnapshotContext();

    const { assureApiAccount } = useAuthStore();
    const { canModify: hasModifyPermission, canEditSnapshot } = manageSnapshotPermission();

    const snapshotStore = useSnapshotStore();

    const [createdDataSource, setCreatedSnapshots] = React.useState<IDisplaySnapshotProps[]>([]);
    const [publishedDataSource, setPublishedSnapshots] = React.useState<IDisplaySnapshotProps[]>([]);

    React.useEffect(() => {
        setCreatedSnapshots(snapshotStore.displayCreatedSnapshots);
    }, [snapshotStore.displayCreatedSnapshots]);

    React.useEffect(() => {
        setPublishedSnapshots(snapshotStore.displayPublishedSnapshots);
    }, [snapshotStore.displayPublishedSnapshots]);

    const reloadData = async () => {
        try {
            await snapshotStore.loadEntities(assureApiAccount.accountId, true);
        } catch (err) {
            message.error(err.message);
        }
    };

    const onDeleteSnapshot = async (snapshotId: number) => {
        try {
            await snapshotStore.deleteSnapshot(assureApiAccount.accountId, snapshotId);
        } catch (err) {
            message.error(err.message);
        }
    };

    const onCreatedSnapshotsSearch = (search = '') => {
        setCreatedSnapshots(
            snapshotStore.displayCreatedSnapshots.filter((item: IDisplaySnapshotProps) =>
                item.label.toLowerCase().includes(search.toLowerCase()),
            ),
        );
    };

    const onPublishedSnapshotsSearch = (search = '') => {
        setPublishedSnapshots(
            snapshotStore.displayPublishedSnapshots.filter((item: IDisplaySnapshotProps) =>
                item.label.toLowerCase().includes(search.toLowerCase()),
            ),
        );
    };

    const onDeleteRow = (snapshotId: number) => {
        confirmDelete('onDelete', async () => onDeleteSnapshot(snapshotId));
    };

    const softwareSnapshotNameColumn = TableUtils.createBasicFilterColumn({
        key: 'label',
        title: 'Software release name',
        searchPlaceholder: 'Software release name',
        columnExtraOptions: {
            width: '45%',
            render(value, snapshot: IDisplaySnapshotProps) {
                const handler = () => {
                    if (snapshot.status == SNAPSHOT_STATUS.RELEASED)
                        return context.toPublishedSnapshotDetails(snapshot.id);
                    return context.toModifySnapshot(snapshot.id);
                };

                return (
                    <Tooltip title={snapshot.description} placement='bottomRight'>
                        <Typography.Link key={snapshot.id} onClick={handler}>
                            {snapshot.label}
                        </Typography.Link>
                    </Tooltip>
                );
            },
        },
    });

    const softwareSnapshotActionsColumn = TableUtils.createExtraColumn({
        key: 'actions',
        title: 'Actions',
        render(text, snapshot: IDisplaySnapshotProps) {
            return (
                <div className={snapshotsCss.actionButtons}>
                    <PublishSnapshotDrawer
                        code={`${snapshot.id}`}
                        title='Publishing Software Release'
                        actionButton={{
                            htmlType: 'button',
                            type: 'link',
                            label: 'Publish',
                            disabled: !canEditSnapshot(snapshot.accountUuid),
                        }}
                        snapshot={snapshot}
                        submitAction={(values, id: number): void => context.toPreviewSnapshot(id)}
                    />
                    <IconTextButton
                        className={snapshotsCss.linkButton}
                        key={snapshot.id}
                        type='link'
                        onClick={(): void => onDeleteRow(snapshot.id)}
                        label='Delete'
                        disabled={!canEditSnapshot(snapshot.accountUuid)}
                    />
                </div>
            );
        },
        columnExtraOptions: {
            width: '15%',
        }
    });

    const softwareSnapshotTypeColumn = TableUtils.createBasicEnumFilterColumn({
        title: 'Type',
        key: 'type',
        filters: Object.keys(FILTER_REPOSITORY_TYPE).map((type) => ({
            text: FILTER_REPOSITORY_TYPE[type],
            value: type,
        })),
        columnExtraOptions: {
            width: '5%',
        }
    });

    const softwareSnapshotVersionColumn = TableUtils.createBasicFilterColumn({
        key: 'version',
        title: 'Version',
        columnExtraOptions: {
            width: '15%',
            sorter: (a: IDisplaySnapshotProps, b: IDisplaySnapshotProps) =>
                a.version.localeCompare(b.version, undefined, { numeric: true }),
        },
    });

    const softwareSnapshotAccountColumn = TableUtils.createBasicFilterColumn({
        title: 'VAR/Client name',
        key: 'accountName',
    });

    const softwareSnapshotCreatedDateColumn = TableUtils.createBasicFilterTimeColumn({
        title: 'Created date',
        key: 'createdAt',
        compareKey: 'createdAtTimestamp',
        defaultSortOrder: 'descend',
    });

    const createdTable: IBasicTableProps = {
        dataSource: createdDataSource,
        columns: [
            softwareSnapshotNameColumn,
            softwareSnapshotTypeColumn,
            softwareSnapshotAccountColumn,
            softwareSnapshotCreatedDateColumn,
            ...(hasModifyPermission ? [softwareSnapshotActionsColumn] : []),
        ],
    };

    const publishedTable: IBasicTableProps = {
        dataSource: publishedDataSource,
        columns: [
            softwareSnapshotNameColumn,
            softwareSnapshotTypeColumn,
            softwareSnapshotVersionColumn,
            softwareSnapshotAccountColumn,
            softwareSnapshotCreatedDateColumn,
        ],
    };

    const onAdd = () => {
        context.toModifySnapshot(undefined);
    };

    const createdTableToolbar: ITableToolbarProps = {
        onSearch: onCreatedSnapshotsSearch,
        onAdd: hasModifyPermission ? onAdd : undefined,
        onReload: reloadData,
        searchPlaceholder: 'Search for a software release',
    };

    const publishedTabletoolbar: ITableToolbarProps = {
        onSearch: onPublishedSnapshotsSearch,
        onReload: reloadData,
        searchPlaceholder: 'Search for a software release',
    };

    return (
        <React.Fragment>
            <Typography.Text>
                Software Releases can be of two types, Created or Published. Created Software Releases can be edited
                (packages added and removed) or the whole release deleted. Once a Software Release has been published,
                it can no longer be edited.
            </Typography.Text>
            <br />
            <br />
            <Label label='Software Releases' strong />
            <BasicTab
                defaultActiveIndex={0}
                activeKey={props.snapshotType}
                onChange={(activeKey: SNAPSHOT_STATUS) => props.setSnapshotType(activeKey)}
                tabPanes={[
                    {
                        label: 'Created',
                        key: SNAPSHOT_STATUS.CREATED,
                        pane: (
                            <AdvanceTable
                                toolbar={createdTableToolbar}
                                title=''
                                loadingState={snapshotStore.dataLoading}
                                table={createdTable}
                            />
                        ),
                    },
                    {
                        label: 'Published',
                        key: SNAPSHOT_STATUS.RELEASED,
                        pane: (
                            <AdvanceTable
                                title=''
                                loadingState={snapshotStore.dataLoading}
                                table={publishedTable}
                                toolbar={publishedTabletoolbar}
                            />
                        ),
                    },
                ]}
            />
        </React.Fragment>
    );
});
